import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  DisplayWrapper,
  Title,
  PinDot,
  ErrorText,
  KeyboardWrapper,
  KeyboardRow,
  NumberButton,
  ActionButton,
  BackspaceButton,
  SubmitButton,
} from 'components/PinVerificationModal/styled';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import { PadRoot, EmployeeIdIndicators } from './styled';

export const EMPLOYEE_ID_MAX_LENGTH = 12;

const isTypingTarget = (target) => {
  return (
    !!target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable)
  );
};

// Stable reference: useBarcodeScanHandler re-registers (and onscan.js
// re-attaches, dropping a scan in progress) whenever its options change, and
// the pad re-renders on every digit.
const SCAN_OPTIONS = {};

// Tapping a key shouldn't leave focus on it: Enter on a focused button
// activates that button, and a badge scan's Enter must still submit.
const keepFocus = (event) => {
  event.preventDefault();
};

/**
 * Keypad for entering an ERP employee ID, as on operator-kiosk. Also takes
 * typed digits, so keyboard-wedge badge scanners (digits then Enter) work.
 *
 * `onSubmit(employeeId)` returns an error message to show, or nothing when the
 * ID was accepted (the pad then clears).
 */
const EmployeeIdPad = ({ onSubmit, enabled, isOpen }) => {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState('');
  const [error, setError] = useState('');

  const employeeIdRef = useRef(employeeId);
  employeeIdRef.current = employeeId;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Set when the current keystroke's Enter submitted, so the scanner's
  // report of the same scan (in the same keydown) isn't submitted again.
  const keySubmittedRef = useRef(false);
  // After a rejected ID, the next digit starts a new entry, so a retried
  // badge scan isn't appended to the one that failed.
  const rejectedRef = useRef(false);
  // More digits arrived than the pad holds; never submit a truncated ID.
  const overflowRef = useRef(false);

  const append = useCallback((digit) => {
    const startOver = rejectedRef.current;
    rejectedRef.current = false;
    if (startOver) overflowRef.current = false;
    setEmployeeId((current) => {
      if (startOver) return digit;
      if (current.length >= EMPLOYEE_ID_MAX_LENGTH) {
        overflowRef.current = true;
        return current;
      }
      return current + digit;
    });
    setError('');
  }, []);

  const backspace = useCallback(() => {
    rejectedRef.current = false;
    overflowRef.current = false;
    setEmployeeId((current) => {
      return current.slice(0, -1);
    });
    setError('');
  }, []);

  const clear = useCallback(() => {
    rejectedRef.current = false;
    overflowRef.current = false;
    setEmployeeId('');
    setError('');
  }, []);

  const submitValue = useCallback(
    (entered) => {
      if (!entered) {
        setError(t('Required'));
        return;
      }
      const submitError = onSubmit(entered);
      if (submitError) {
        rejectedRef.current = true;
        setError(submitError);
        return;
      }
      clear();
    },
    [onSubmit, clear, t]
  );

  // Returns false when the entry overflowed and wasn't submitted.
  const submit = useCallback(() => {
    if (overflowRef.current) {
      rejectedRef.current = true;
      setError(t('Employee ID is too long'));
      return false;
    }
    submitValue(employeeIdRef.current);
    return true;
  }, [submitValue, t]);

  // With barcode scanning on, the global scanner (onscan.js) also sees a
  // badge scan and would route it to the page underneath; claim it while
  // sign-in is open. The pad normally submits the scan from its own Enter;
  // this submits it when that Enter went to a focused key instead.
  const handleScan = useCallback(
    (scanned) => {
      if (!enabledRef.current || keySubmittedRef.current) return;
      submitValue(String(scanned).trim());
    },
    [submitValue]
  );

  useBarcodeScanHandler('employee-id-pad', handleScan, {
    priority: BARCODE_HANDLER_PRIORITIES.EMPLOYEE_ID_PAD,
    enabled: isOpen,
    scanOptions: SCAN_OPTIONS,
  });

  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (event) => {
      keySubmittedRef.current = false;
      if (isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const { key } = event;
      if (key >= '0' && key <= '9') {
        event.preventDefault();
        append(key);
      } else if (key === 'Backspace' || key === 'Delete') {
        event.preventDefault();
        backspace();
      } else if (key === 'Enter') {
        // A keyboard user on a focused key means that key. (A scanned
        // badge's Enter there is submitted by handleScan instead.)
        if (event.target && event.target.tagName === 'BUTTON') return;
        event.preventDefault();
        // An overflowed scan is left to handleScan, which gets the full ID.
        keySubmittedRef.current = submit();
      }
    };

    // Capture on window so the pad sees each key before onscan.js, which
    // listens on document and stops propagation of a scan's final Enter.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [enabled, append, backspace, submit]);

  const renderDigit = (digit) => {
    return (
      <NumberButton
        type="button"
        onMouseDown={keepFocus}
        onClick={() => {
          return append(digit);
        }}
      >
        {digit}
      </NumberButton>
    );
  };

  return (
    <PadRoot id="employee-id-pad">
      <DisplayWrapper>
        <Title>{t('Enter Employee ID')}</Title>
        <EmployeeIdIndicators
          data-testid="employee-id-dots"
          role="img"
          aria-label={t('Digits entered: {{count}}', {
            count: employeeId.length,
          })}
        >
          {Array.from(employeeId).map((digit, index) => {
            // eslint-disable-next-line react/no-array-index-key
            return <PinDot key={index} />;
          })}
        </EmployeeIdIndicators>
        {/* The placeholder only reserves the line's height. */}
        <ErrorText
          $visible={!!error}
          role={error ? 'alert' : undefined}
          aria-hidden={!error}
        >
          {error || 'Error'}
        </ErrorText>
      </DisplayWrapper>

      <KeyboardWrapper>
        <KeyboardRow>
          {renderDigit('1')}
          {renderDigit('2')}
          {renderDigit('3')}
        </KeyboardRow>
        <KeyboardRow>
          {renderDigit('4')}
          {renderDigit('5')}
          {renderDigit('6')}
        </KeyboardRow>
        <KeyboardRow>
          {renderDigit('7')}
          {renderDigit('8')}
          {renderDigit('9')}
        </KeyboardRow>
        <KeyboardRow>
          <ActionButton type="button" onMouseDown={keepFocus} onClick={clear}>
            {t('Clear')}
          </ActionButton>
          {renderDigit('0')}
          <BackspaceButton
            type="button"
            onMouseDown={keepFocus}
            onClick={backspace}
            aria-label={t('Backspace')}
          >
            ←
          </BackspaceButton>
        </KeyboardRow>
      </KeyboardWrapper>

      <SubmitButton
        variant="primary"
        onMouseDown={keepFocus}
        onClick={() => {
          submit();
        }}
      >
        {t('Submit')}
      </SubmitButton>
    </PadRoot>
  );
};

EmployeeIdPad.propTypes = {
  /** Returns an error message, or nothing when the ID was accepted. */
  onSubmit: PropTypes.func.isRequired,
  /** Listen for typed digits / badge scans (off while a dialog is on top). */
  enabled: PropTypes.bool.isRequired,
  /** Sign-in is open: keep badge scans away from the pages underneath. */
  isOpen: PropTypes.bool.isRequired,
};

export { EmployeeIdPad };
